import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useLiveView } from "./useLiveView";
import { LiveViewContext } from "./LiveViewContext";
import { VIEW_LIVE_PARAM } from "./LivePresenceDot";

/** How long to wait for presence before telling the user the applicant left. */
const VIEW_LIVE_PRESENCE_WAIT_MS = 5_000;

// The drawer brings rrweb; load it on the first "View live", not at boot.
const LiveViewDrawer = lazy(() =>
  import("./LiveViewDrawer").then((module) => ({
    default: module.LiveViewDrawer
  }))
);

/**
 * Opens one `/live-view` socket for the application page and hosts the
 * viewer drawer. Unmounting it (leaving the page) stops any open session.
 */
export const LiveViewProvider = ({
  applicationId,
  children
}: {
  applicationId: string | undefined;
  children: ReactNode;
}) => {
  const liveView = useLiveView(applicationId);
  const { watch: requestWatch, stop } = liveView;
  const [opened, setOpened] = useState(false);
  // The row's name, until presence (the server's copy) is available.
  const [fallbackName, setFallbackName] = useState("");
  // Gives every watch a fresh replay stage.
  const [attempt, setAttempt] = useState(0);

  const watch = useCallback(
    (applicant: { id: number; name: string }) => {
      if (!requestWatch(applicant.id)) return;
      setAttempt((current) => current + 1);
      setFallbackName(applicant.name);
      setOpened(true);
    },
    [requestWatch]
  );

  // "View live" from the applications table (?view_live=<applicant id>): ask
  // that applicant once they show up in presence, then drop the param.
  const [searchParams, setSearchParams] = useSearchParams();
  const viewLiveParam = searchParams.get(VIEW_LIVE_PARAM);
  const { enabled, connected, presence } = liveView;
  useEffect(() => {
    if (!viewLiveParam || !enabled || !connected) return undefined;
    const clearParam = () => {
      const next = new URLSearchParams(searchParams);
      next.delete(VIEW_LIVE_PARAM);
      setSearchParams(next, { replace: true });
    };
    const entry = presence.get(Number(viewLiveParam));
    if (entry) {
      clearParam();
      watch({ id: entry.applicant_id, name: entry.applicant_name });
      return undefined;
    }
    const timer = setTimeout(() => {
      clearParam();
      toast.info("The applicant isn't in the application anymore.");
    }, VIEW_LIVE_PRESENCE_WAIT_MS);
    return () => clearTimeout(timer);
  }, [
    viewLiveParam,
    enabled,
    connected,
    presence,
    watch,
    searchParams,
    setSearchParams
  ]);

  const closeViewer = useCallback(() => {
    stop();
    setOpened(false);
  }, [stop]);

  const value = useMemo(
    () => (liveView.enabled ? { presence: liveView.presence, watch } : null),
    [liveView.enabled, liveView.presence, watch]
  );

  return (
    <LiveViewContext.Provider value={value}>
      {children}
      {/* attempt > 0: someone has clicked "View live" on this page. */}
      {liveView.enabled && attempt > 0 ? (
        <Suspense fallback={null}>
          <LiveViewDrawer
            opened={opened}
            liveView={liveView}
            attempt={attempt}
            fallbackName={fallbackName}
            onAskAgain={(applicantId) =>
              watch({ id: applicantId, name: fallbackName })
            }
            onClose={closeViewer}
          />
        </Suspense>
      ) : null}
    </LiveViewContext.Provider>
  );
};
